import React, { useState, useEffect, useRef } from "react";
import styles from "./SlidingBox.module.css"; // Import the same styles used by isBoxVisible
import ExclamationIcon from '../../assets/notification';

interface SlidingBoxProps {
    title: string;
    message: string;
    duration?: number; // Optional duration for how long the box stays visible (default: 5 seconds)
    onClose?: () => void; // Optional callback when the box closes
    headerRef: React.RefObject<HTMLDivElement>;
    isFAQPaneOpen: boolean; 
    backgroundColor?: string; 
}

const SlidingBox: React.FC<SlidingBoxProps> = ({ title, message, duration = 5000, onClose, headerRef, isFAQPaneOpen, backgroundColor, }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isSlidingIn, setIsSlidingIn] = useState(false);
    const [isSlidingOut, setIsSlidingOut] = useState(false);
    const boxRef = useRef<HTMLDivElement>(null);
    const headerContainerRef = headerRef;

    useEffect(() => {
        calculateBoxPosition();
        window.addEventListener('resize', calculateBoxPosition);

        return () => {
            window.removeEventListener('resize', calculateBoxPosition);
        };
    }, []);

    // Function to calculate and set CSS variables for sliding positions
    const calculateBoxPosition = () => {
        const boxHeight = boxRef.current ? boxRef.current.offsetHeight : 100;
        const headerHeight = headerContainerRef.current ? headerContainerRef.current.offsetHeight : 0;
        const slideInPosition = headerHeight;
        const initialPosition = -boxHeight;

        document.documentElement.style.setProperty('--initial-position', `${initialPosition}px`);
        document.documentElement.style.setProperty('--slide-in-position', `${slideInPosition}px`);
    };

    const calculateLeftPosition = () => {
        if (isFAQPaneOpen && window.innerWidth >= 992) {
             return '65%';
         }
         return '50%';
     };

    useEffect(() => {
        calculateBoxPosition();
        setIsVisible(true);
    
        // Start with isSlidingIn as false to ensure the box starts at the initial position
        setIsSlidingIn(false);
        setIsSlidingOut(false);
    
        // Delay setting isSlidingIn to true to trigger the slide-in animation
        const slideInTimer = setTimeout(() => {
            setIsSlidingIn(true);
        }, 10); // Small delay to allow the initial render to complete
    
        // After 'duration', switch to sliding out
        const slideOutTimer = setTimeout(() => {
            setIsSlidingOut(true);
            setIsSlidingIn(false);
        }, duration);
    
        // Hide the box after animation completes
        const hideTimer = setTimeout(() => {
            setIsVisible(false);
            setIsSlidingOut(false);
            if (onClose) onClose();
        }, duration + 300);
    
        window.addEventListener('resize', calculateBoxPosition);
    
        return () => {
            clearTimeout(slideInTimer);
            clearTimeout(slideOutTimer);
            clearTimeout(hideTimer);
            window.removeEventListener('resize', calculateBoxPosition);
        };
    }, [duration, onClose]);

    if (!isVisible) return null;

    return (
        <div
            ref={boxRef}
            className={`${styles.box} ${isSlidingIn ? styles.slideIn : ""} ${isSlidingOut ? styles.slideOut : ""}`}
            style={{
                left: calculateLeftPosition(), // Center the box horizontally
                transform: "translateX(-50%)", // Adjust for centering
                backgroundColor: backgroundColor || 'var(--YW_Body_Signout_Notification_Background_Color)',
            }}
        >
            <ExclamationIcon className={styles.icon} />
            <div
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
                className={styles.textcontainer}
            >
                <strong>{title}</strong>
                <p>{message}</p>
            </div>
        </div>
    );
};

export default SlidingBox;